import type { ReactNode } from 'react';

import type { ColumnProps } from '../types';

/**
 * Column component for defining table columns.
 *
 * This is a declarative/config component that returns null.
 * Column configuration is parsed by parseChildren in the parent DataTable
 * component during render - not by effects in this component.
 *
 * @example Basic column
 * ```tsx
 * <DataTable.Column id="name" accessor="name">
 *   <DataTable.Header>Name</DataTable.Header>
 * </DataTable.Column>
 * ```
 *
 * @example Column with custom cell
 * ```tsx
 * <DataTable.Column id="status" accessor="status">
 *   <DataTable.Header>Status</DataTable.Header>
 *   <DataTable.Cell>
 *     {({ value }) => <StatusBadge status={value} />}
 *   </DataTable.Cell>
 * </DataTable.Column>
 * ```
 *
 * @example Full-featured column
 * ```tsx
 * <DataTable.Column
 *   id="status"
 *   accessor="status"
 *   width={150}
 *   minWidth={100}
 *   sortable
 *   pinned="left"
 *   filterType="multi-select"
 *   filterOptions={['active', 'inactive']}
 *   mobile={{ hidden: true }}
 * >
 *   <DataTable.Header>Status</DataTable.Header>
 *   <DataTable.Cell>
 *     {({ value }) => <StatusBadge status={value} />}
 *   </DataTable.Cell>
 * </DataTable.Column>
 * ```
 */
export const Column = <TData, TValue = unknown>(_props: ColumnProps<TData, TValue>): ReactNode => {
  // Pure declarative component - configuration is parsed by parseChildren
  // in the parent DataTable, not by effects in this component
  return null;
};

Column.displayName = 'DataTable.Column';
